import React, { memo, useMemo } from 'react'
import classnames from 'classnames'
import { ISelectOption, SelectOption, SelectBase } from 'components/forms/Selects'
import { IconButton } from 'transfix-ui/components/IconButton'
import uuid from 'uuid'
import { DownArrowIcon, CloseIcon } from 'transfix-icons'
import 'scss/5_components/_single-select.scss'

export interface ISingleSelectProps {
  id: string
  dataTest: string
  icon?: JSX.Element
  label: string
  options: ISelectOption[]
  onSelect: (selected?: ISelectOption) => void
  className?: string
  selected?: ISelectOption | string | number
  /**
   * When `options` array is empty, value of
   * `emptyStateLabel` will display in an empty option element
   * @example 'No saved customers'
   */
  emptyStateLabel?: string
  /**
   * @default true
   */
  clearable?: boolean
  /**
   * @default true
   */
  showSelectedOptionLabel?: boolean
}

export const SingleSelect = memo<ISingleSelectProps>(
  ({
    icon,
    label,
    options = [],
    id,
    dataTest,
    onSelect,
    className,
    selected,
    emptyStateLabel,
    clearable = true,
    showSelectedOptionLabel = true,
  }) => {
    const clearSelection = () => onSelect(undefined)
    const interactionIcon =
      clearable && selected ? (
        <IconButton
          onClick={clearSelection}
          Icon={CloseIcon}
          color='tertiary'
          noPadding
          label='clear filter'
          className='pa0 clear-filter'
          data-test='clear-filter'
          size='large'
        />
      ) : (
        <DownArrowIcon size='medium' />
      )

    const selectOptionHandler = (newSelectedValue: ISelectOption) => onSelect(newSelectedValue)

    const singleSelectClass = classnames('dib', className)

    const selectedOption = useMemo(() => {
      if (typeof selected === 'string' || typeof selected === 'number') {
        return options.find(o => o.value === selected)
      }
      return selected
    }, [options, selected])

    const selectedOptionLabel = useMemo(() => selectedOption?.label ?? label, [
      label,
      selectedOption,
    ])

    const noOp = () => {}

    const textClasses = classnames('pr2 nowrap', {
      'ml4 pl2': icon,
      'text-default': !selected,
    })

    return (
      <SelectBase
        hideOnSelect
        id={id}
        dataTest={dataTest}
        selectButtonType='filter-pill'
        applied={!!selected}
        className={singleSelectClass}
        displayValue={
          <div className='flex flex-row items-center relative'>
            <div className='pr2 prefix-icon'>{icon}</div>
            <b className={textClasses}>{showSelectedOptionLabel ? selectedOptionLabel : label}</b>
            {interactionIcon}
          </div>
        }>
        <>
          {options.map(
            ({ icon: optionIcon, isLink: linkValue, label: optionLabel, value: optionValue }) => (
              <SelectOption
                key={uuid()}
                label={optionLabel}
                selected={selectedOption?.value === optionValue}
                value={optionValue}
                onSelect={selectOptionHandler}
                dataTest={`${dataTest}-option-${optionValue}`}
                icon={optionIcon}
                isLink={linkValue}
              />
            )
          )}
          {!options.length && !!emptyStateLabel && (
            <SelectOption
              key={uuid()}
              label={emptyStateLabel}
              selected={false}
              value='-1'
              onSelect={noOp}
              dataTest={`${dataTest}-empty-state`}
              isEmptyState
            />
          )}
        </>
      </SelectBase>
    )
  }
)

export default SingleSelect
